import { baseTheme, freshmarkTheme, inputsTheme, villaTheme } from 'design-system';
import { isFreshmarkEnabled } from 'config/features';
import { AdminProfile } from 'generated/graphql';
import isCustomScheme from '../utils/is-custom-scheme';

export enum ThemeVariant {
  FreshMark = AdminProfile.FreshMark,
  Nile = AdminProfile.Nile,
  Inputs = AdminProfile.NileInputs,
  Villa = AdminProfile.Villa,
}

export const NILE_ADMIN_PROFILE_ID = 1;
export const FRESHMARK_ADMIN_PROFILE_ID = 2;
export const INPUTS_ADMIN_PROFILE_ID = 3;
export const VILLA_ADMIN_PROFILE_ID = 4;
export const CATALOG_SELLER_ID = 30000;

export const adminProfileToIdMap: { [key: string]: number } = {
  [AdminProfile.Nile]: NILE_ADMIN_PROFILE_ID,
  [AdminProfile.FreshMark]: FRESHMARK_ADMIN_PROFILE_ID,
  [AdminProfile.NileInputs]: INPUTS_ADMIN_PROFILE_ID,
  [AdminProfile.Villa]: VILLA_ADMIN_PROFILE_ID,
};

export const themeVariantToAdminProfileIdMap = {
  [ThemeVariant.Nile]: NILE_ADMIN_PROFILE_ID,
  [ThemeVariant.FreshMark]: FRESHMARK_ADMIN_PROFILE_ID,
  [ThemeVariant.Inputs]: INPUTS_ADMIN_PROFILE_ID,
  [ThemeVariant.Villa]: VILLA_ADMIN_PROFILE_ID,
};

export const getAdminProfileFromVariant = () => {
  const adminProfileID = themeVariantToAdminProfileIdMap[getThemeVariant()]?.toString();
  return adminProfileID && adminProfileID.length > 0 ? adminProfileID : NILE_ADMIN_PROFILE_ID.toString();
};

const getStorybookThemeVariant = () => {
  // Get the story path from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const storyBookGlobals = urlParams.get('globals')?.toLowerCase();

  if (!storyBookGlobals) return null;

  if (storyBookGlobals?.includes('freshmark')) {
    return ThemeVariant.FreshMark;
  }
  if (storyBookGlobals?.includes('inputs')) {
    return ThemeVariant.Inputs;
  }
  if (storyBookGlobals?.includes('villa')) {
    return ThemeVariant.Villa;
  }
  return ThemeVariant.Nile;
};

export const getThemeVariant = (
  location: {
    pathname: string;
    hash: string;
  } = window.location,
) => {
  const storybookThemeVariant = getStorybookThemeVariant();

  if (storybookThemeVariant) {
    return storybookThemeVariant;
  }
  // The hash fragment my contain a search string, thus we only check the start segment.
  if (location.pathname.includes('freshmark') && isFreshmarkEnabled) {
    return ThemeVariant.FreshMark;
  }
  if (location.pathname.includes('inputs')) {
    return ThemeVariant.Inputs;
  }
  if (window.location.origin.includes('villa') || location.pathname.includes('villa')) {
    return ThemeVariant.Villa;
  }
  return ThemeVariant.Nile;
};

export const getThemeClassName = () => {
  const themeVariant = getThemeVariant();
  if (themeVariant === ThemeVariant.FreshMark && isFreshmarkEnabled) return 'freshmark';
  if (themeVariant === ThemeVariant.Inputs) return 'inputs';
  if (themeVariant === ThemeVariant.Villa) return 'villa';
  return 'nile';
};

export const getRoutePrefix = () => {
  const variant = getThemeVariant();
  if (variant === ThemeVariant.FreshMark && isFreshmarkEnabled) return '/freshmark';
  if (variant === ThemeVariant.Inputs) return '/inputs';
  if (variant === ThemeVariant.Villa) return '/villa';
  return '';
};

export const stripRoutePrefix = (path: string) => {
  for (const prefix of ['/freshmark', '/inputs', '/villa']) {
    if (path.startsWith(prefix)) return path.slice(prefix.length) || '/';
  }
  return path;
};

export const getURLTheme = (url?: string) => {
  // Get the story path from URL parameters - StoryBook theming
  const urlParams = new URLSearchParams(window.location.search);
  const storyBookGlobals = urlParams.get('globals')?.toLowerCase();

  const path = storyBookGlobals || url || getRoutePrefix();

  if (path.includes('freshmark') && isFreshmarkEnabled) return freshmarkTheme;
  if (path.includes('inputs')) return inputsTheme;
  if (path.includes('villa')) return villaTheme;
  return baseTheme;
};

export const getTheme = (variant: AdminProfile) => {
  if (variant === AdminProfile.FreshMark && isFreshmarkEnabled) return freshmarkTheme;
  if (variant === AdminProfile.NileInputs) return inputsTheme;
  if (variant === AdminProfile.Villa) return villaTheme;
  return baseTheme;
};

/**
 * Returns the route with the given prefix applied. When explicitPrefix is omitted, uses the current theme's prefix from getRoutePrefix().
 * Normalizes same-origin full URLs to paths so callers can use navigate(path) without extra handling.
 */
export const getPrefixedRoute = (url?: string, explicitPrefix?: string) => {
  if (isCustomScheme(url!)) {
    // custom scheme so we don't want the browser to redirect
    return '';
  }

  // Normalize same-origin full URLs to paths so navigate(path) works and logic can stay simple
  if (url?.startsWith(window.location.origin)) {
    url = url.slice(window.location.origin.length) || '/';
  }

  const prefix = explicitPrefix !== undefined ? explicitPrefix : getRoutePrefix();

  if (url?.startsWith('#')) {
    return url.replace('#', `${prefix}`);
  }

  if (url?.startsWith('/#/')) {
    return url.replace('/#/', `${prefix}/`);
  }

  if (url?.startsWith('/')) {
    // Path may already include prefix when derived from same-origin URL
    if (url.startsWith(prefix)) return url;
    return `${prefix}${url}`;
  }

  return url as string;
};

export const getPlatformTitle = () => {
  const themeVariant = getThemeVariant();
  return themeVariant === ThemeVariant.Villa ? 'Villa' : 'Nile';
};
